import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { DateTime, Info } from 'luxon';
import Box from '../Box/Box';
import Text from '../Text/Text';
import Icon from '../Icon/Icon';
import DatePickerHeader, {
  DatePickerHeaderContent,
  DatePickerHeaderNext,
  DatePickerHeaderPrevious,
} from './DatePickerHeader';

const DatePickerMonthItem = ({
  'aria-label': label,
  children,
  id,
  n,
  onClick,
  selected,
}: {
  id?: string;
  'aria-label': string;
  children: ReactNode;
  selected: boolean;
  n: number;
  onClick: (n: number) => void;
}) => {
  const { day, text } = useMultiStyleConfig('DatePickerDay', {
    currentMonth: true,
    selectable: true,
    selection: selected ? 'incomplete' : undefined,
  });
  return (
    <Box
      aria-label={label}
      aria-selected={selected ? true : undefined}
      as="button"
      id={id}
      onClick={() => onClick(n)}
      role="option"
      sx={day}
      type="button"
      width="48"
    >
      <Text sx={{ ...text, width: '48' }}>{children}</Text>
    </Box>
  );
};

const monthNames = Info.months('short');
const longMonthNames = Info.months('long');
const DatePickerMonthSelector = ({
  onMonthSelected,
  viewDate,
}: {
  viewDate: DateTime;
  onMonthSelected: (month: number, year: number) => void;
}): JSX.Element => {
  const [selectedYear, setSelectedYear] = useState(viewDate.year);
  const onPreviousYear = useCallback(() => setSelectedYear((year) => year - 1), []);
  const onNextYear = useCallback(() => setSelectedYear((year) => year + 1), []);
  const yearRef = useRef<HTMLInputElement>(null);
  useEffect(() => yearRef.current?.focus(), []);
  const monthClicked = useCallback((m: number) => onMonthSelected(m, selectedYear), [selectedYear]);
  const monthId = useId();
  return (
    <Box>
      <DatePickerHeader onNext={onNextYear} onPrevious={onPreviousYear}>
        <DatePickerHeaderPrevious label="previous year" />
        <DatePickerHeaderContent>
          <NumberInput
            aria-label="year"
            flexShrink={1}
            max={2100}
            min={1990}
            onChange={(_, year) => {
              setSelectedYear(year);
            }}
            size="small"
            value={selectedYear}
            w="4.5rem"
          >
            <NumberInputField ref={yearRef} />
            <NumberInputStepper>
              <NumberIncrementStepper>
                <Icon name="ChevronUp" size="16" />
              </NumberIncrementStepper>
              <NumberDecrementStepper>
                <Icon name="ChevronDown" size="16" />
              </NumberDecrementStepper>
            </NumberInputStepper>
          </NumberInput>
        </DatePickerHeaderContent>
        <DatePickerHeaderNext label="next year" />
      </DatePickerHeader>
      <Box
        alignItems="center"
        aria-activedescendant={monthId}
        aria-label="month"
        display="grid"
        gridTemplateColumns="repeat(3,1fr)"
        gridTemplateRows="repeat(4, 1fr)"
        justifyItems="center"
        role="listbox"
        rowGap="24"
      >
        {monthNames.map((month, idx) => {
          const selected = viewDate.month === idx + 1;
          return (
            <DatePickerMonthItem
              key={month}
              aria-label={longMonthNames[idx]}
              id={selected ? monthId : undefined}
              n={idx + 1}
              onClick={monthClicked}
              selected={selected}
            >
              {month}
            </DatePickerMonthItem>
          );
        })}
      </Box>
    </Box>
  );
};
export default DatePickerMonthSelector;
